import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * Tooltip component with Tailwind CSS — replaces react-bootstrap OverlayTrigger + Tooltip.
 * Wraps a single trigger element; shows on hover/focus with a configurable delay.
 *
 * Usage:
 *   <Tooltip content="Save changes" placement="top">
 *     <button>...</button>
 *   </Tooltip>
 *
 * Positioning is absolute within an inline-flex wrapper (no portal), so inside an
 * overflow-hidden container prefer placement that stays in view.
 */
const PLACEMENT_CLASSES = {
  top: 'tw-bottom-full tw-left-1/2 tw--translate-x-1/2 tw-mb-1.5',
  bottom: 'tw-top-full tw-left-1/2 tw--translate-x-1/2 tw-mt-1.5',
  left: 'tw-right-full tw-top-1/2 tw--translate-y-1/2 tw-mr-1.5',
  right: 'tw-left-full tw-top-1/2 tw--translate-y-1/2 tw-ml-1.5',
};

const ARROW_CLASSES = {
  top: 'tw-top-full tw-left-1/2 tw--translate-x-1/2 tw-border-t-slate-800 tw-border-x-transparent tw-border-b-transparent',
  bottom: 'tw-bottom-full tw-left-1/2 tw--translate-x-1/2 tw-border-b-slate-800 tw-border-x-transparent tw-border-t-transparent',
  left: 'tw-left-full tw-top-1/2 tw--translate-y-1/2 tw-border-l-slate-800 tw-border-y-transparent tw-border-r-transparent',
  right: 'tw-right-full tw-top-1/2 tw--translate-y-1/2 tw-border-r-slate-800 tw-border-y-transparent tw-border-l-transparent',
};

const Tooltip = ({
  content,
  placement = 'top',
  delay = 200,
  hideDelay = 0,
  className = '',
  wrapperClassName = '',
  children,
}) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const show = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setVisible(true), delay);
  }, [clearTimer, delay]);

  const hide = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setVisible(false), hideDelay);
  }, [clearTimer, hideDelay]);

  const hasContent = content !== null && content !== undefined && content !== '';

  return (
    <span
      className={`tw-relative tw-inline-flex ${wrapperClassName}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {hasContent && visible && (
        <span
          role="tooltip"
          className={`tw-absolute tw-z-50 tw-px-2.5 tw-py-1.5 tw-rounded-sm tw-bg-slate-800 tw-text-white tw-text-xs tw-font-medium tw-whitespace-nowrap tw-shadow-lg tw-pointer-events-none ${PLACEMENT_CLASSES[placement]} ${className}`}
        >
          {content}
          <span
            aria-hidden="true"
            className={`tw-absolute tw-border-4 tw-border-solid ${ARROW_CLASSES[placement]}`}
          />
        </span>
      )}
    </span>
  );
};

Tooltip.propTypes = {
  content: PropTypes.node,
  placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  delay: PropTypes.number,
  hideDelay: PropTypes.number,
  className: PropTypes.string,
  wrapperClassName: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default Tooltip;
